import React, { useRef, useEffect, useState } from 'react';
import { Box, Paper, Typography, CircularProgress } from '@mui/material';
import { ComposedChart, Bar, XAxis, YAxis, Tooltip, Legend, Line, ReferenceLine, CartesianGrid, LabelList, Cell, ResponsiveContainer } from 'recharts';
import { dashboardStyles } from '../../../styles/dashboard';
import DateRangePicker from '../common/DateRangePicker';
import SummaryCard from '../common/SummaryCard';
import { MonthlyData, ItemSalesData } from '../../../types/dashboard';
import { formatCurrency } from '../../../utils/dashboard';
import dayjs, { Dayjs } from 'dayjs';
import html2pdf from 'html2pdf.js';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { PieChart, Pie, Legend as RechartsLegend, Tooltip as RechartsTooltip, ResponsiveContainer as PieResponsiveContainer } from 'recharts';
import cachedApi from '../../../utils/cachedApi';

interface SalesTabProps {
  dateRange: {
    start: Dayjs;
    end: Dayjs;
  };
  onDateChange: (type: 'start' | 'end', value: Dayjs | null) => void;
  monthlyData: MonthlyData[];
  itemSalesData: ItemSalesData[];
  loading: boolean;
  itemSalesLoading: boolean;
  error: string | null;
  itemSalesError: string | null;
}

const SalesTab: React.FC<SalesTabProps> = ({
  dateRange,
  onDateChange,
  monthlyData,
  itemSalesData,
  loading,
  itemSalesLoading,
  error,
  itemSalesError
}) => {
  const exportRef = useRef<HTMLDivElement>(null);

  // 거래처 TOP7 매출 도넛차트용 상태
  const [companyTop7, setCompanyTop7] = useState<{ company: string; totalBilling: number }[]>([]);
  const [companyTop7Loading, setCompanyTop7Loading] = useState(false);
  const [companyTop7Error, setCompanyTop7Error] = useState<string | null>(null);

  // 도넛차트 색상 배열 상수화
  const DONUT_COLORS = ["#ff4444","#ffc107","#4caf50","#2196f3","#9c27b0","#00bcd4","#ff5722"];

  useEffect(() => {
    // 기간 내 거래처별 매출액 합산 후 TOP7 추출
    const fetchTop7 = async () => {
      setCompanyTop7Loading(true);
      setCompanyTop7Error(null);
      try {
        // 캐시된 API 사용
        // const data = await cachedApi.getMonthlyCompanyBilling();
        // 기간 필터링
        const start = dateRange.start.format('YYYY-MM');
        const end = dateRange.end.format('YYYY-MM');
        const res = await fetch(`/api/dashboard/monthly-company-billing?start=${start}&end=${end}`);
        if (!res.ok) throw new Error('데이터를 불러오지 못했습니다');
        const data = await res.json();
        const filtered = data.filter((row: any) => row.month >= start && row.month <= end);
        // 거래처별 매출액 합산
        const companyMap: Record<string, number> = {};
        filtered.forEach((row: any) => {
          if (!row.company) return;
          companyMap[row.company] = (companyMap[row.company] || 0) + Number(row.totalBilling || 0);
        });
        const top7 = Object.entries(companyMap)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 7)
          .map(([company, totalBilling]) => ({ company, totalBilling }));
        setCompanyTop7(top7);
      } catch (e: any) {
        console.error('거래처 TOP7 조회 실패:', e);
        setCompanyTop7Error(e.message);
        
        // 폴백: 기존 방식
        try {
          const fallbackStart = dateRange.start.format('YYYY-MM');
          const fallbackEnd = dateRange.end.format('YYYY-MM');
          const res = await fetch(`/api/dashboard/monthly-company-billing?start=${fallbackStart}&end=${fallbackEnd}`);
          if (!res.ok) throw new Error('거래처 매출 데이터를 불러오지 못했습니다');
          const data = await res.json();
          const filtered = data.filter((row: any) => row.month >= fallbackStart && row.month <= fallbackEnd);
          const companyMap: Record<string, number> = {};
          filtered.forEach((row: any) => {
            if (!row.company) return;
            companyMap[row.company] = (companyMap[row.company] || 0) + Number(row.totalBilling || 0);
          });
          const top7 = Object.entries(companyMap)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 7)
            .map(([company, totalBilling]) => ({ company, totalBilling }));
          setCompanyTop7(top7);
        } catch (fallbackError: any) {
          setCompanyTop7Error(fallbackError.message);
        }
      } finally {
        setCompanyTop7Loading(false);
      }
    };
    fetchTop7();
  }, [dateRange.start, dateRange.end]);

  // 월별 매출액: 기간 필터링
  const filteredMonthlyData = monthlyData.filter(row => 
    row.month >= dateRange.start.format('YYYY-MM') && 
    row.month <= dateRange.end.format('YYYY-MM')
  );

  // 전년 동월 대비 증감률을 포함한 데이터 생성
  const filteredMonthlyDataWithDiff = filteredMonthlyData.map(row => {
    const prevYearMonth = dayjs(row.month + '-01').subtract(1, 'year').format('YYYY-MM');
    const prev = monthlyData.find(r => r.month === prevYearMonth);
    if (!prev || prev.totalBilling === 0) {
      return { ...row, prevYearDiffRate: null };
    }
    const diff = row.totalBilling - prev.totalBilling;
    const diffRate = (diff / prev.totalBilling) * 100;
    return { ...row, prevYearDiffRate: diffRate };
  });

  // 평균 청구운송비 계산
  const avgTotalBilling =
    filteredMonthlyDataWithDiff.length > 0
      ? filteredMonthlyDataWithDiff.reduce((sum, row) => sum + row.totalBilling, 0) / filteredMonthlyDataWithDiff.length
      : 0;

  // 품목별 매출액: 기간 필터링 후 품목별 합산, 상위 10개 (미기입·공란 제외)
  const itemSalesByName: { [itemName: string]: number } = {};
  itemSalesData.forEach(row => {
    const name = row.itemName != null ? String(row.itemName).trim() : '';
    if (name === '' || name === '(미기입)') return;
    if (row.month >= dateRange.start.format('YYYY-MM') && row.month <= dateRange.end.format('YYYY-MM')) {
      itemSalesByName[name] = (itemSalesByName[name] || 0) + row.totalBilling;
    }
  });

  const top10ItemSales = Object.entries(itemSalesByName)
    .map(([itemName, totalBilling]) => ({ itemName, totalBilling }))
    .sort((a, b) => b.totalBilling - a.totalBilling)
    .slice(0, 10);

  // PDF 내보내기: 현재 보이는 영역을 PDF로 저장
  const handleExportPDF = () => {
    if (!exportRef.current) return;
    html2pdf().set({
      margin: 0.5,
      filename: `매출대시보드_${dateRange.start.format('YYYY-MM')}_${dateRange.end.format('YYYY-MM')}.pdf`,
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'cm', format: 'a4', orientation: 'landscape' },
      pagebreak: { mode: ['avoid-all'] }
    }).from(exportRef.current).save();
  };

  return (
    <>
      {/* 날짜 선택 및 요약 정보 */}
      <Paper elevation={0} sx={dashboardStyles.paper}>
        <Box sx={{ 
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <DateRangePicker
            startDate={dateRange.start}
            endDate={dateRange.end}
            onStartDateChange={(date) => onDateChange('start', date)}
            onEndDateChange={(date) => onDateChange('end', date)}
          />
          {/* PDF 버튼만 남김 */}
          <Box sx={{ display: 'flex', gap: 1, ml: 1 }}>
            <button
              style={{ background: '#1976d2', color: '#fff', border: 'none', borderRadius: 5, padding: '8px 18px', fontWeight: 600, cursor: 'pointer' }}
              onClick={handleExportPDF}
            >
              PDF
            </button>
          </Box>
          {/* 합계 정보 카드 */}
          {filteredMonthlyData.length > 0 && (
            <Box sx={{ 
              display: 'flex',
              gap: 1,
              ml: 'auto'
            }}>
              <SummaryCard
                title="청구운송비 합계"
                value={filteredMonthlyData.reduce((sum, item) => sum + item.totalBilling, 0)}
                color="#ff4444"
              />
              <SummaryCard
                title="손익 합계"
                value={filteredMonthlyData.reduce((sum, item) => sum + item.profit, 0)}
                color="#ffc107"
              />
            </Box>
          )}
        </Box>
      </Paper>

      {/* 차트/표 전체 영역을 exportRef로 감싸기 */}
      <div ref={exportRef} style={{ display: 'flex', flexDirection: 'column', gap: 8, overflow: 'visible' }}>
        {/* 월별 매출액 차트 */}
        <Paper elevation={0} sx={dashboardStyles.paper}>
          <Typography variant="h6" sx={dashboardStyles.title}>
            월별 매출액
          </Typography>

          {loading ? (
            <Box sx={dashboardStyles.loadingContainer}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ color: 'error.main', p: 2 }}>{error}</Box>
          ) : monthlyData.length > 0 ? (
            <Box sx={dashboardStyles.chartContainer}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={filteredMonthlyDataWithDiff}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="month" tick={{ fill: '#888', fontSize: 12 }} />
                  <YAxis 
                    yAxisId="left"
                    tick={{ fill: '#888', fontSize: 12 }}
                    tickFormatter={(value) => formatCurrency(value)}
                  />
                  <Tooltip 
                    content={({ active, payload, label }) => {
                      if (!active || !payload || payload.length === 0) return null;
                      // payload: [{dataKey, name, value, ...}]
                      const billing = payload.find(p => p.dataKey === 'totalBilling');
                      const profit = payload.find(p => p.dataKey === 'profit');
                      return (
                        <div style={{ background: '#2a2a2a', border: '1px solid #333', color: '#fff', padding: 10, borderRadius: 6 }}>
                          <div style={{ fontWeight: 700, marginBottom: 4 }}>{label}</div>
                          {billing && (
                            <div style={{ color: '#ff4444', fontWeight: 700 }}>
                              청구운송비 : {formatCurrency(typeof billing.value === 'number' ? billing.value : 0)}
                            </div>
                          )}
                          {profit && (
                            <div style={{ color: '#ffc107', fontWeight: 700 }}>
                              손익 : {formatCurrency(typeof profit.value === 'number' ? profit.value : 0)}
                            </div>
                          )}
                        </div>
                      );
                    }}
                  />
                  <Legend 
                    verticalAlign="top" 
                    height={36}
                    iconType="circle"
                    wrapperStyle={{ color: '#888' }}
                  />
                  <Bar 
                    dataKey="totalBilling" 
                    name="청구운송비" 
                    fill="#ff4444"
                    radius={[4, 4, 0, 0]}
                    yAxisId="left"
                  >
                    <LabelList
                      dataKey="totalBilling"
                      position="top"
                      formatter={(value: number, entry: any) => formatCurrency(value)}
                      style={{ fontSize: 14, fill: '#ff4444', fontWeight: 700, textShadow: '0 0 2px #000' }}
                    />
                    <LabelList
                      dataKey="prevYearDiffRate"
                      position="top"
                      content={({ x, y, value, width }) => {
                        if (typeof value !== 'number') return null;
                        const isUp = value > 0;
                        const color = isUp ? '#ff4444' : '#2196f3';
                        const yPos = typeof y === 'number' ? y - 36 : -36;
                        const xPos = typeof x === 'number' && typeof width === 'number' ? x + width / 2 : x;
                        return (
                          <text
                            x={xPos}
                            y={yPos}
                            fill={color}
                            fontWeight="900"
                            fontSize="16"
                            textAnchor="middle"
                          >
                            {isUp ? '▲' : '▼'}{Math.abs(value).toFixed(1)}%
                          </text>
                        );
                      }}
                    />
                  </Bar>
                  <Bar 
                    dataKey="profit" 
                    name="손익" 
                    fill="#ffc107"
                    radius={[4, 4, 0, 0]}
                    yAxisId="left"
                  >
                    <LabelList
                      dataKey="profit"
                      position="top"
                      formatter={(value: number) => formatCurrency(value)}
                      style={{ fontSize: 14, fill: '#ffc107', fontWeight: 700, textShadow: '0 0 2px #000' }}
                    />
                  </Bar>
                  <Line
                    type="monotone"
                    dataKey="totalBilling"
                    name="추세선"
                    stroke="#2196f3"
                    strokeWidth={2}
                    dot={{ fill: '#2196f3', r: 4 }}
                    yAxisId="left"
                  />
                  <ReferenceLine
                    y={avgTotalBilling}
                    yAxisId="left"
                    stroke="#00bcd4"
                    strokeDasharray="6 3"
                    label={({ viewBox }) => {
                      const { width, y } = viewBox;
                      return (
                        <text
                          x={width - 10}
                          y={y - 8}
                          fill="#00bcd4"
                          fontWeight={700}
                          fontSize={15}
                          textAnchor="end"
                        >
                          {`평균: ${formatCurrency(avgTotalBilling)}`}
                        </text>
                      );
                    }}
                  />
                  <ReferenceLine y={0} stroke="#444" yAxisId="left" />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <Box sx={dashboardStyles.loadingContainer}>
              데이터가 없습니다.
            </Box>
          )}
        </Paper>

        {/* 거래처 TOP7 매출 도넛차트 */}
        <Paper elevation={0} sx={dashboardStyles.paper}>
          <Typography variant="h6" sx={dashboardStyles.title}>
            거래처별 매출액 TOP 7
          </Typography>
          {companyTop7Loading ? (
            <Box sx={dashboardStyles.loadingContainer}><CircularProgress /></Box>
          ) : companyTop7Error ? (
            <Box sx={{ color: 'error.main', p: 2 }}>{companyTop7Error}</Box>
          ) : companyTop7.length > 0 ? (
            <Box sx={{ width: '100%', height: 360, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <PieResponsiveContainer width="100%" height={360}>
                <PieChart>
                  <Pie
                    data={companyTop7}
                    dataKey="totalBilling"
                    nameKey="company"
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={120}
                    label={({ name, percent }) => percent > 0.03 ? `${name} (${(percent * 100).toFixed(1)}%)` : ''}
                    labelLine={props => {
                      if (!props?.value) return <g />;
                      return <path {...props} />;
                    }}
                    isAnimationActive={false}
                  >
                    {companyTop7.map((entry, idx) => (
                      <Cell key={`cell-${idx}`} fill={DONUT_COLORS[idx % DONUT_COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip formatter={(value: number) => `${formatCurrency(value)}`} />
                  <RechartsLegend />
                </PieChart>
              </PieResponsiveContainer>
            </Box>
          ) : (
            <Box sx={dashboardStyles.loadingContainer}>데이터가 없습니다.</Box>
          )}
        </Paper>

        {/* 품명별 매출액 차트 */}
        <Paper elevation={0} sx={dashboardStyles.paper}>
          <Typography variant="h6" sx={dashboardStyles.title}>
            품명별 매출액 TOP 10
          </Typography>

          {itemSalesLoading ? (
            <Box sx={dashboardStyles.loadingContainer}>
              <CircularProgress />
            </Box>
          ) : itemSalesError ? (
            <Box sx={{ color: 'error.main', p: 2 }}>{itemSalesError}</Box>
          ) : itemSalesData.length > 0 ? (
            <Box sx={dashboardStyles.chartContainer}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={top10ItemSales}
                  layout="vertical"
                  margin={{ top: 10, right: 20, left: -70, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis
                    type="number"
                    tickFormatter={(value) => formatCurrency(value)}
                    tick={{ fill: '#fff', fontSize: 13, fontWeight: 700 }}
                    xAxisId="bottom"
                  />
                  <YAxis
                    dataKey="itemName"
                    type="category"
                    tick={{ fill: '#fff', fontSize: 13, fontWeight: 700 }}
                    width={150}
                  />
                  <Tooltip
                    formatter={(_value: number, _name: string, props) => {
                      const val = typeof props.value === 'number' ? props.value : 0;
                      return [
                        <span style={{ color: '#ff4444', fontWeight: 700 }}>
                          매출액: {formatCurrency(val)}
                        </span>,
                        ''
                      ];
                    }}
                    contentStyle={{
                      backgroundColor: '#2a2a2a',
                      border: '1px solid #333',
                      color: '#fff'
                    }}
                    labelStyle={{ color: '#fff' }}
                  />
                  <Bar
                    dataKey="totalBilling"
                    name="매출액"
                    radius={[0, 4, 4, 0]}
                    xAxisId="bottom"
                  >
                    <LabelList
                      dataKey="totalBilling"
                      position="outside"
                      formatter={(value: number) => formatCurrency(value)}
                      style={{ fontSize: 12, fill: '#fff', fontWeight: 700, textShadow: '0 0 2px #000' }}
                    />
                    {top10ItemSales.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={[
                          '#ff4444',
                          '#ffc107',
                          '#4caf50',
                          '#2196f3',
                          '#9c27b0',
                          '#00bcd4',
                          '#ff5722',
                          '#3f51b5',
                          '#009688',
                          '#795548'
                        ][index]}
                      />
                    ))}
                  </Bar>
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <Box sx={dashboardStyles.loadingContainer}>
              데이터가 없습니다.
            </Box>
          )}
        </Paper>

        {/* 월별 매출 표 (거래처매출 표와 동일 스타일) */}
        <TableContainer component={Paper} sx={{ mt: 1, ...dashboardStyles.table }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={dashboardStyles.tableHeaderCell}>날짜</TableCell>
                <TableCell sx={dashboardStyles.tableHeaderCell}>매출액</TableCell>
                <TableCell sx={dashboardStyles.tableHeaderCell}>지불액</TableCell>
                <TableCell sx={dashboardStyles.tableHeaderCell}>손익</TableCell>
                <TableCell sx={dashboardStyles.tableHeaderCell}>전년 동월 대비 증감률</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMonthlyData.map((row, idx) => {
                const prevYearMonth = dayjs(row.month + '-01').subtract(1, 'year').format('YYYY-MM');
                const prev = monthlyData.find(r => r.month === prevYearMonth);
                const diff: number = prev ? row.totalBilling - prev.totalBilling : 0;
                const diffRate = prev && prev.totalBilling !== 0 ? (diff / prev.totalBilling) * 100 : null;
                return (
                  <TableRow key={row.month || idx}>
                    <TableCell sx={dashboardStyles.tableCell}>{row.month}</TableCell>
                    <TableCell sx={dashboardStyles.tableCell}>{formatCurrency(row.totalBilling)}</TableCell>
                    <TableCell sx={dashboardStyles.tableCell}>{formatCurrency(row.totalPayment)}</TableCell>
                    <TableCell sx={dashboardStyles.tableCell}>{formatCurrency(row.profit)}</TableCell>
                    <TableCell sx={dashboardStyles.tableCell}>
                      {diffRate !== null ? (
                        <span style={{ color: diffRate > 0 ? '#ff4444' : '#2196f3', fontWeight: 700 }}>
                          {diffRate > 0 ? '▲' : '▼'}{Math.abs(diffRate).toFixed(1)}%
                        </span>
                      ) : '-'}
                    </TableCell>
                  </TableRow>
                );
              })}
              {/* 합계 행 */}
              <TableRow>
                <TableCell sx={{ ...dashboardStyles.tableCell, fontWeight: 700 }}>합계</TableCell>
                <TableCell sx={{ ...dashboardStyles.tableCell, fontWeight: 700 }}>
                  {formatCurrency(filteredMonthlyData.reduce((sum, row) => sum + row.totalBilling, 0))}
                </TableCell>
                <TableCell sx={{ ...dashboardStyles.tableCell, fontWeight: 700 }}>
                  {formatCurrency(filteredMonthlyData.reduce((sum, row) => sum + row.totalPayment, 0))}
                </TableCell>
                <TableCell sx={{ ...dashboardStyles.tableCell, fontWeight: 700 }}>
                  {formatCurrency(filteredMonthlyData.reduce((sum, row) => sum + row.profit, 0))}
                </TableCell>
                <TableCell sx={{ ...dashboardStyles.tableCell, fontWeight: 700 }}>-</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </>
  );
};

export default SalesTab; 